import type {
  DeliveryHistoryItemType,
  OrderDeliveryHistoryEntryType,
  OrderDeliveryHistoryResponseType,
} from "@sellar-io/api-v2/types";
import { Button } from "@sellar-io/components";
import {
  formatCurrency,
  getDisplayNameByKey,
  orderableItemValues,
} from "@sellar-io/common";
import type { ReactNode } from "react";
import { useOrderDocumentDownload } from "../../../hooks/api/v2/orderDocument/useOrderDocumentDownload";
import { downloadPDF } from "../../../utils/downloadHelpers";

import { historyDate, timestamp } from "./deliveryHistoryDates";
import { scheduleChangeLabel } from "./scheduleChangeLabel";

type History = OrderDeliveryHistoryResponseType;
type Entry = OrderDeliveryHistoryEntryType;

function Items({
  items,
  heading,
}: {
  items: DeliveryHistoryItemType[];
  heading?: string;
}) {
  if (!items.length) {
    return null;
  }
  return (
    <div>
      {heading && <p className="mb-1 font-semibold">{heading}</p>}
      <ul className="space-y-1">
        {items.map((item) => {
          const format = item.format;
          const sizeParts = [];
          if (format) {
            sizeParts.push(
              format.packQuantity && format.packQuantity > 1
                ? `${format.packQuantity} ×`
                : null,
              format.volume != null
                ? `${format.volume}${format.displayUnit ?? ""}`
                : null,
              format.containerType
                ? (getDisplayNameByKey(
                    orderableItemValues,
                    format.containerType,
                  ) ?? format.containerType)
                : null,
            );
          }
          const size = sizeParts.filter(Boolean).join(" ");
          return (
            <li key={item.orderItemId}>
              {item.quantity} × {item.productName}
              {size && ` · ${size}`}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function deliveryIssueHeading(
  issue: Extract<Entry, { type: "issue" }>,
  buyer: boolean,
) {
  if (issue.kind === "failedCollection") {
    return buyer
      ? "We couldn’t collect your order"
      : "We couldn’t collect this order";
  }
  if (issue.noItemsDeliverable) {
    return buyer ? "Your order is unavailable" : "This order is unavailable";
  }
  return buyer
    ? "We’re sorry, we can’t deliver your order in full"
    : "Some items can’t be delivered";
}

function reasonText(
  reason: Extract<Entry, { type: "issue" }>["reasonGroups"][number]["reason"],
  history: History,
) {
  const buyer = history.audience === "buyer";
  switch (reason) {
    case "unavailableAtCollection":
      return buyer
        ? `${history.supplierName} wasn’t able to provide these items when we arrived to collect your order.`
        : "Saleable stock wasn’t available when our driver arrived to collect these items.";
    case "lostInTransit":
      return buyer
        ? "These items were lost on the way to you."
        : "These items were lost in transit.";
    case "damagedInTransit":
      return buyer
        ? "These items were damaged on the way to you and can’t be delivered in a suitable condition."
        : "These items were damaged in transit and can’t be delivered in a suitable condition.";
    case "unavailable":
      return "These items are unavailable and won’t be delivered as part of this order.";
  }
}

function label(entry: Entry, history: History): string {
  const buyer = history.audience === "buyer";
  switch (entry.type) {
    case "issue":
      return entry.actor === "driver"
        ? "Issue recorded by delivery driver"
        : "Issue recorded by the Sellar Distribution team";
    case "decision": {
      const choice =
        entry.choice === "wait" ? "wait for the order" : "request cancellation";
      if (entry.actor === "operator") {
        return `The Sellar team recorded a decision to ${choice} on behalf of ${buyer ? "you" : history.buyerName}`;
      }
      if (entry.actor === "buyer") {
        return `${buyer ? "You" : history.buyerName} chose to ${choice}`;
      }
      return `A decision to ${choice} was recorded`;
    }
    case "estimate":
      return "Updated estimate recorded";
    case "scheduled":
      return scheduleChangeLabel(entry);
    case "collected":
    case "palletReceived": {
      const outcome =
        entry.type === "collected"
          ? "Order marked as collected"
          : "Pallet received";
      if (entry.actor === "driver") {
        return `${outcome} by delivery driver`;
      }
      if (entry.actor === "distributionTeam") {
        return `${outcome} by the Sellar Distribution team`;
      }
      return outcome;
    }
    case "delivered":
      return entry.deliveredItems.length
        ? "Order marked as delivered"
        : "Delivery outcome recorded — no items delivered";
    case "cancelled":
      return "Order cancelled";
    case "voided":
      return "Order voided";
    case "creditNote":
      return "Credit note issued";
    case "creditAdjustment":
      return "Sellar Credit adjustment completed";
    case "externalRefund":
      return "Supplier refund confirmation recorded";
    case "refund":
      return "Refund recorded";
  }
}

function Details({ children }: { children: ReactNode }) {
  return (
    <div className="mt-3 space-y-4 rounded bg-zinc-100 p-4 text-sm text-zinc-800">
      {children}
    </div>
  );
}

function CreditNote({
  entry,
}: {
  entry: Extract<Entry, { type: "creditNote" }>;
}) {
  const download = useOrderDocumentDownload();
  return (
    <Details>
      <p>
        A credit note for {formatCurrency(entry.amount)} has been issued.
        Issuance does not itself confirm a completed credit adjustment or
        refund.
      </p>
      <Button
        size="small"
        color="secondary"
        loading={download.isPending}
        disabled={download.isPending}
        onClick={() =>
          download.mutate(
            { id: entry.document.id, type: entry.document.type },
            {
              onSuccess: (result) =>
                downloadPDF(
                  `Credit note - ${entry.document.reference ?? entry.document.id}.pdf`,
                  result.document,
                ),
            },
          )
        }
      >
        Download credit note
        {entry.document.reference ? ` ${entry.document.reference}` : ""}
      </Button>
      {download.isError && (
        <p role="alert" className="text-red-600">
          We couldn’t download this credit note. Please try again.
        </p>
      )}
    </Details>
  );
}

function EntryDetails({ entry, history }: { entry: Entry; history: History }) {
  const buyer = history.audience === "buyer";
  switch (entry.type) {
    case "issue":
      return (
        <Details>
          <p className="font-semibold">{deliveryIssueHeading(entry, buyer)}</p>
          {entry.reasonGroups.map((group, index) => (
            <div key={`${group.reason}:${index}`} className="space-y-2">
              <p>{reasonText(group.reason, history)}</p>
              <Items items={group.items} />
            </div>
          ))}
          {entry.noItemsDeliverable && entry.kind !== "failedCollection" && (
            <p>No items remain deliverable under this order.</p>
          )}
          {entry.kind !== "failedCollection" && (
            <div>
              <p className="mb-1 font-semibold">
                What we recommend you do next
              </p>
              <p>
                Please contact{" "}
                {buyer ? history.supplierName : history.buyerName} to agree what
                happens next for{" "}
                {entry.noItemsDeliverable ? "this order" : "the affected items"}
                .
              </p>
            </div>
          )}
        </Details>
      );
    case "estimate": {
      const date = buyer ? entry.deliveryAt : entry.collectionAt;
      const stage = buyer ? "delivery" : "collection";
      return (
        <p className="mt-2 text-sm text-zinc-600">
          {date
            ? `Updated estimated ${stage} date: ${historyDate(date)}.`
            : `We’ll confirm the updated ${stage} arrangements.`}
        </p>
      );
    }
    case "scheduled": {
      const actors = {
        operator: "the Sellar team",
        supplier: history.supplierName,
        buyer: history.buyerName,
        system: "Sellar’s planning system",
        driver: "the delivery driver",
      };
      return (
        <div className="mt-2 space-y-2 text-sm text-zinc-600">
          {entry.actor && <p>Recorded by {actors[entry.actor]}.</p>}
          {!entry.scheduledDate && (
            <p>
              A new date is to be confirmed. Removing a booking does not cancel
              the order.
            </p>
          )}
        </div>
      );
    }
    case "delivered":
      return (
        <Details>
          <Items heading="Items delivered" items={entry.deliveredItems} />
          <Items heading="Items NOT delivered" items={entry.undeliveredItems} />
        </Details>
      );
    case "voided":
      return (
        <Details>
          <p className="font-semibold">
            {buyer
              ? "We’re sorry, your order can’t be delivered"
              : "This order can’t be delivered"}
          </p>
          <p>
            This order has been voided, so no items will be delivered under this
            order.
          </p>
          <Items heading="Items not being delivered" items={entry.items} />
          <p className="font-semibold">
            {buyer
              ? "What happens to your payment"
              : "What happens to the buyer’s payment"}
          </p>
          {entry.paymentHandling === "sellarCredit" ? (
            <p>
              {entry.hasIssuedCreditNotes
                ? "Recorded credit notes are listed in this history. They do not alone confirm a completed credit adjustment or refund."
                : "A credit note will be issued for any remaining uncredited value of the invoice. No action is needed from you."}
            </p>
          ) : (
            <p>
              {buyer
                ? `Please contact ${history.supplierName} to arrange any credit notes or refunds directly with them.`
                : `Please handle any credit notes or refunds directly with your customer. Please contact ${history.buyerName} if you can help them arrange a replacement order.`}
            </p>
          )}
        </Details>
      );
    case "creditNote":
      return <CreditNote entry={entry} />;
    case "creditAdjustment":
      return (
        <Details>
          <p>
            A Sellar Credit adjustment of {formatCurrency(entry.amount)} has
            been recorded as completed.
          </p>
        </Details>
      );
    case "externalRefund":
      return (
        <Details>
          <p>
            {buyer
              ? `${history.supplierName} has confirmed that they refunded ${formatCurrency(entry.amount)} for this order.`
              : `You recorded an external refund of ${formatCurrency(entry.amount)} for this order.`}{" "}
            This is the supplier’s confirmation, not independent verification of
            settlement.
          </p>
        </Details>
      );
    case "refund":
      return (
        <Details>
          <p>
            A refund of {formatCurrency(entry.amount)} was recorded for this
            order.
          </p>
        </Details>
      );
    default:
      return null;
  }
}

export function DeliveryHistoryEntry({
  entry,
  history,
}: {
  entry: Entry;
  history: History;
}) {
  return (
    <li className="relative border-l-2 border-zinc-200 pb-6 pl-6 last:border-transparent last:pb-0">
      <span
        aria-hidden="true"
        className="absolute top-1 -left-[7px] h-3 w-3 rounded-full bg-zinc-300"
      />
      <p className="text-sm leading-6">
        {label(entry, history)}{" "}
        <time dateTime={new Date(entry.recordedAt).toISOString()}>
          on {timestamp(entry.recordedAt)}
        </time>
      </p>
      <EntryDetails entry={entry} history={history} />
    </li>
  );
}
